import type { CmsReview } from '@erp/shared';
import { Check, MessageSquare, Star, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Card, Skeleton } from '../../components/shadcn/index.js';
import { Badge, Button, confirmDialog, DataTable, type DataTableColumn, EmptyState } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { formatDate } from '../../lib/intl.js';

function Stars({ value }: { value: number }): JSX.Element {
  return (
    <span className="inline-flex">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`h-3.5 w-3.5 ${n <= value ? 'fill-amber-400 text-amber-400' : 'text-fg-subtle'}`} aria-hidden />
      ))}
    </span>
  );
}

/** Staff moderation of storefront product reviews. */
export function CmsReviewsPage(): JSX.Element {
  const { t, i18n } = useTranslation('cms');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('cms.write');
  const [items, setItems] = useState<CmsReview[] | null>(null);

  const reload = async (): Promise<void> => {
    const r = await api().get<{ items: CmsReview[] }>('/cms/reviews');
    setItems(r.data.items);
  };
  useEffect(() => { void reload(); }, []);

  const approve = async (r: CmsReview, approved: boolean): Promise<void> => {
    try { await api().put(`/cms/reviews/${r.id}/approve`, { approved }); await reload(); } catch { toast.error(tc('errors:server')); }
  };
  const remove = async (r: CmsReview): Promise<void> => {
    if (!(await confirmDialog({ message: t('reviews.confirmDelete', { defaultValue: 'Delete this review?' }), tone: 'danger' }))) return;
    try { await api().delete(`/cms/reviews/${r.id}`); await reload(); } catch { toast.error(tc('errors:server')); }
  };

  if (!items) return <Card className="p-6"><Skeleton className="h-6 w-48" /><Skeleton className="mt-4 h-40 w-full" /></Card>;

  const columns: DataTableColumn<CmsReview>[] = [
    { id: 'product', header: t('reviews.product', { defaultValue: 'Product' }), sortValue: (r) => r.itemName ?? '', searchValue: (r) => `${r.itemName ?? ''} ${r.name}`, cell: (r) => <span className="font-medium">{r.itemName ?? '—'}</span> },
    { id: 'reviewer', header: t('reviews.reviewer', { defaultValue: 'By' }), sortValue: (r) => r.name, cell: (r) => r.name },
    { id: 'rating', header: t('reviews.rating', { defaultValue: 'Rating' }), sortValue: (r) => r.rating, cell: (r) => <Stars value={r.rating} /> },
    { id: 'comment', header: t('reviews.comment', { defaultValue: 'Comment' }), cellClassName: 'max-w-sm', cell: (r) => <span className="line-clamp-2 text-fg-muted">{r.comment ?? '—'}</span> },
    { id: 'date', header: t('reviews.date', { defaultValue: 'Date' }), sortValue: (r) => r.createdAt, cellClassName: 'text-fg-muted text-xs', cell: (r) => formatDate(r.createdAt, { locale: i18n.language }) },
    { id: 'status', header: t('reviews.status', { defaultValue: 'Status' }), sortValue: (r) => (r.isApproved ? 1 : 0),
      cell: (r) => <Badge tone={r.isApproved ? 'success' : 'warning'}>{r.isApproved ? t('reviews.approved', { defaultValue: 'Approved' }) : t('reviews.pending', { defaultValue: 'Pending' })}</Badge> },
    ...(canWrite ? [{
      id: 'actions', header: '', align: 'end' as const, headClassName: 'w-32',
      cell: (r: CmsReview) => (
        <div className="flex justify-end gap-1">
          {r.isApproved ? (
            <Button variant="ghost" size="sm" onClick={() => void approve(r, false)} title={t('reviews.hide', { defaultValue: 'Hide' })}><X className="h-4 w-4" aria-hidden /></Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => void approve(r, true)} title={t('reviews.approve', { defaultValue: 'Approve' })}><Check className="h-4 w-4" aria-hidden /></Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => void remove(r)} title={tc('actions.delete')}><Trash2 className="h-4 w-4" aria-hidden /></Button>
        </div>
      ),
    }] : []),
  ];

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">{t('reviews.title', { defaultValue: 'Product reviews' })}</h1>
        <p className="mt-1 text-sm text-fg-muted">{t('reviews.description', { defaultValue: 'Approve customer reviews before they appear on the storefront.' })}</p>
      </header>
      {items.length === 0 ? (
        <EmptyState icon={<MessageSquare className="h-8 w-8" aria-hidden />} title={t('reviews.empty', { defaultValue: 'No reviews yet' })} description={t('reviews.emptyDescription', { defaultValue: 'Reviews customers leave on your store will show here for approval.' })} />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable columns={columns} data={items} rowKey={(r) => r.id} rowClassName={(r) => (r.isApproved ? undefined : 'bg-warning/5')} searchPlaceholder={t('reviews.search', { defaultValue: 'Search reviews…' })} />
        </Card>
      )}
    </div>
  );
}
